/**
 * Club header behaviour: transparent-to-solid on scroll, mobile menu, live cart count.
 *
 * Uses document-level listeners and re-queries the DOM each time, so it keeps working when the
 * theme editor re-renders the header section.
 */

const MOBILE_QUERY = window.matchMedia('(max-width: 989px)');

/** Scroll position of whichever element scrolls (the window on mobile, .page-wrapper on desktop). */
function getScrollTop() {
  const wrapper = document.querySelector('.page-wrapper');
  return Math.max(window.scrollY || 0, wrapper ? wrapper.scrollTop : 0);
}

function updateScrolledState() {
  const header = document.querySelector('[data-cc-header]');
  if (!header || !header.classList.contains('cc-header--overlay')) return;
  header.classList.toggle('is-scrolled', getScrollTop() > 40);
}

function setMobileMenu(open) {
  const menu = document.querySelector('[data-cc-mobile-menu]');
  if (!menu) return;

  menu.classList.toggle('is-open', open);
  menu.toggleAttribute('inert', !open);
  document.documentElement.classList.toggle('cc-no-scroll', open);

  if (open) {
    menu.querySelector('[data-cc-menu-close]')?.focus();
  } else {
    document.querySelector('[data-cc-menu-open]')?.focus({ preventScroll: true });
  }
}

function setCartCount(count) {
  for (const el of document.querySelectorAll('[data-cc-cart-count]')) {
    el.textContent = String(count);
  }
}

async function refreshCartCount() {
  const source = document.querySelector('[data-cc-cart]')?.getAttribute('data-cart-js');
  if (!source) return;

  try {
    const response = await fetch(source, { headers: { Accept: 'application/json' } });
    const cart = await response.json();
    if (typeof cart.item_count === 'number') setCartCount(cart.item_count);
  } catch (_) {
    // keep the server-rendered count
  }
}

document.addEventListener('scroll', updateScrolledState, { passive: true, capture: true });
document.addEventListener('shopify:section:load', updateScrolledState);
updateScrolledState();

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  if (target.closest('[data-cc-menu-open]')) {
    setMobileMenu(true);
  } else if (target.closest('[data-cc-menu-close]')) {
    setMobileMenu(false);
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') setMobileMenu(false);
});

MOBILE_QUERY.addEventListener('change', (event) => {
  if (!event.matches) setMobileMenu(false);
});

window.addEventListener('pageshow', (event) => {
  if (event.persisted) refreshCartCount();
});

// Keep the [ 0 ] count in sync with the theme's cart events (add to cart, quantity changes, removals).
import('@shopify/events')
  .then(({ StandardEvents }) => {
    document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      event.promise
        ?.then(({ cart, detail }) => {
          const count = cart?.totalQuantity ?? detail?.itemCount;
          if (typeof count === 'number') setCartCount(count);
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') console.warn('[cc-header] cart update failed:', error);
        });
    });
  })
  .catch(() => {
    // Standard events unavailable: the count refreshes on the next page load instead.
  });
